<template>
  <ValidationObserver #default="{ handleSubmit }">
    <FilterManager
      :value="filter_proxy"
      :filters="filters"
      :open-advanced.sync="advancedFilter"
      @academicYear="setFilterAcademicYear"
      @center="setFilterCenter"
      @municipality="setFilterMunicipality"
      @school="setFilterSchool"
      @category="setFilterCategory"
      @name="setFilterName"
      @grade="setFilterGrade"
      @search="handleSubmit(onSearch)"
    />
  </ValidationObserver>
</template>

<script>
import FilterManager from 'FilterManager';
import * as api from '@/views/pei/location-alternatives/_api';
import { initialFilter, initialCatalog, initialPagination } from '../_utils';
import { PAGE_SIZE } from '../_config';
import codeService from '@/modules/common/code.api';

export default {
  name: 'LocationAlternativesSearchFilter',
  components: { FilterManager },
  props: {
    result: { type: Array, default: () => [], required: true },
    pagination: {
      type: Object,
      default: initialPagination,
      required: true,
    },
  },
  data: () => ({
    filter: initialFilter(),
    catalog: initialCatalog(),
    advancedFilter: false,
  }),
  computed: {
    filter_proxy: {
      get() {
        return { ...initialFilter(), ...this.filter };
      },
      set(filter) {
        this.filter = filter;
      },
    },
    filters() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.catalog.academicYears,
          value: this.filter.academicYear,
          fieldKey: 'id',
          fieldName: 'description',
          class: 'col-12 col-md-3',
          placeholder: 'Año Academico:',
          clearable: false,
          validations: { required: true },
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.catalogByFilterCriteria.centers,
          value: this.filter.center,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-3',
          placeholder: 'Centro',
          clearable: true,
          disabled: this.filter.academicYear === null,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.catalogByFilterCriteria.municipalities,
          value: this.filter.municipality,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-3',
          placeholder: 'Municipio',
          clearable: true,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          options: this.catalogByFilterCriteria.schools,
          value: this.filter.school,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-3',
          placeholder: 'Escuela',
          clearable: true,
          disabled: this.filter.municipality === null,
        },
        {
          name: 'category',
          component: 'CustomDropDown',
          options: this.catalog.categories,
          value: this.filter.category,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-4',
          label: 'Categoría de la Alternativa:',
          placeholder: 'Seleccione',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'name',
          component: 'CustomDropDown',
          options: this.catalogByFilterCriteria.names,
          value: this.filter.name,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-4',
          label: 'Nombre de la Alternativa:',
          placeholder: 'Seleccione',
          clearable: true,
          disabled: this.filter.category === null,
          isAdvanced: true,
        },
        {
          name: 'grade',
          component: 'CustomDropDown',
          options: this.catalog.grades,
          value: this.filter.grade,
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-12 col-md-4',
          label: 'Grado:',
          placeholder: 'Seleccione',
          clearable: true,
          isAdvanced: true,
        },
      ];
    },
    catalogByFilterCriteria() {
      const {
        academicYear = null,
        center = null,
        municipality = null,
        category = null,
      } = this.filter;
      const { centers, municipalities, schools, names } = this.catalog;
      return {
        centers: academicYear
          ? centers?.filter(
              ({ academicYearId }) => academicYearId === academicYear.id
            )
          : [],
        municipalities: center
          ? municipalities?.filter(({ centerId }) => centerId === center.id)
          : municipalities,
        schools: municipality
          ? schools?.filter(
              ({ municipalityId }) => municipalityId === municipality.id
            )
          : [],
        names: category
          ? names?.filter(({ categoryId }) => categoryId === category.id)
          : [],
      };
    },
  },
  methods: {
    setFilter(filter) {
      this.setFilterAcademicYear(filter.academicYear, true);
      this.setFilterCenter(filter.center);
      this.setFilterMunicipality(filter.municipality);
      this.setFilterSchool(filter.school);
      this.setFilterCategory(filter.category);
      this.setFilterName(filter.name);
      this.setFilterGrade(filter.grade);
    },
    setAdvancedFilter(value) {
      this.advancedFilter = value;
    },
    async initFilter() {
      const currentAcademicYear = await api.getCurrentAcademicYear();
      await this.setFilterAcademicYear(currentAcademicYear, false);
    },
    initCatalog() {
      return Promise.all([
        this.getCatalogAcademicYears(),
        this.getCatalogCenters(),
        this.getCatalogMunicipalities(),
      ]);
    },
    async getCatalogAcademicYears() {
      const academicYears = await api.getAcademicYears();
      this.catalog.academicYears = academicYears;
    },
    async getCatalogCenters() {
      if (this.catalogByFilterCriteria?.centers?.length) return;
      const { academicYear = null } = this.filter;
      const centers = academicYear
        ? await api.getCenters({
            academicYearId: academicYear.id,
          })
        : [];
      this.catalog.centers = [...this.catalog.centers, ...centers];
    },
    async getCatalogMunicipalities() {
      if (this.catalogByFilterCriteria?.municipalities?.length) return;
      const { academicYear = null, center = null } = this.filter;
      const municipalities =
        academicYear || center
          ? await api.getMunicipalities({
              academicYearId: academicYear.id,
              centerId: center?.id,
            })
          : [];
      this.catalog.municipalities = [
        ...this.catalog.municipalities,
        ...municipalities,
      ];
    },
    async getCatalogSchools() {
      if (this.catalogByFilterCriteria?.schools?.length) return;
      const {
        academicYear = null,
        center = null,
        municipality = null,
      } = this.filter;
      const schools =
        academicYear && center && municipality
          ? await api.getSchools({
              academicYearId: academicYear.id,
              centerId: center?.id,
              municipalityId: municipality.id,
            })
          : [];
      this.catalog.schools = [...this.catalog.schools, ...schools];
    },
    async getCatalogCategories() {
      if (this.catalog?.categories?.length) return;
      const categories = await api.getCategories();
      this.catalog.categories = categories;
    },
    async getCatalogNames() {
      if (this.catalogByFilterCriteria?.names?.length) return;
      const { category = null } = this.filter;
      const names = category
        ? await api.getNames({
            categoryId: category.id,
          })
        : [];
      this.catalog.names = [...this.catalog.names, ...names];
    },
    async getCatalogGrades() {
      await codeService
        .getGrades({
          ids: [34], // Pei Sin Grado 34
        })
        .then((res) => {
          this.catalog.grades = res;
        });
    },
    async setFilterAcademicYear(academicYear, skip = false) {
      if (this.filter.academicYear !== academicYear) {
        this.filter.academicYear = academicYear;
        this.filter.center = null;
        this.filter.municipality = null;
        this.filter.school = null;
      }
      if (!skip) {
        await this.getCatalogCenters();
      }
    },
    setFilterCenter(center) {
      if (this.filter.center !== center) {
        this.filter.center = center;
        this.filter.municipality = null;
        this.filter.school = null;
      }
      this.getCatalogMunicipalities();
    },
    setFilterMunicipality(municipality) {
      if (this.filter.municipality !== municipality) {
        this.filter.municipality = municipality;
        this.filter.school = null;
      }
      this.getCatalogSchools();
    },
    setFilterSchool(school) {
      this.filter.school = school;
    },
    setFilterCategory(category) {
      if (this.filter.category !== category) {
        this.filter.category = category;
        this.filter.name = null;
      }
      this.getCatalogNames();
    },
    setFilterName(name) {
      this.filter.name = name;
    },
    setFilterGrade(grade) {
      this.filter.grade = grade;
    },
    onSearch() {
      this.search(initialPagination);
    },
    async search(newPagination) {
      const {
        academicYear,
        center,
        municipality,
        school,
        category,
        name,
        grade,
      } = this.filter;
      const { currentPage } = newPagination || this.pagination;
      const filter = {
        academicYearId: academicYear?.id,
        centerId: center?.id,
        municipalityId: municipality?.id,
        schoolId: school?.id,
        categoryId: category?.id,
        nameId: name?.id,
        gradeId: grade?.id,
        pageIndex: currentPage,
        pageSize: PAGE_SIZE,
      };
      const { data, ...pagination } = await api.searchLocationAlternatives(
        filter
      );
      this.$emit('update:result', data);
      this.$emit('update:pagination', pagination);
      this.$emit('update:filter', this.filter);
      this.$emit('update:advancedFilter', this.advancedFilter);
    },
  },
  watch: {
    async advancedFilter(value) {
      if (!value) {
        this.setFilterCategory(null);
        this.setFilterName(null);
      }
      await Promise.all([
        value ? this.getCatalogCategories() : null,
        value ? this.getCatalogGrades() : null,
      ]);
    },
  },
  async mounted() {
    await this.initCatalog();
  },
};
</script>
